<script setup>
import { ref, onMounted } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { useCurrency } from '@/composables/currency'
import { formatDuration } from '@/composables/report'
import { useAccountingStore } from '@/stores/accounting.store'
import { useWebSiteStore } from '@/stores/web-site.store'

const props = defineProps({
  report: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['recomputed', 'cancel'])

const i18n = getI18n()
const { getCurrencyString } = useCurrency()
const accountingStore = useAccountingStore()
const webSiteStore = useWebSiteStore()

const preview = ref(null)
const isSaving = ref(false)

const money = (value) => getCurrencyString(+(value || 0), webSiteStore.getDefaultCurrency()?.code)
const lines = () => [
  { label: i18n.t('print.report.columns.days'), before: preview.value.before.total_days, after: preview.value.after.total_days },
  { label: i18n.t('print.report.columns.gross'), before: formatDuration(preview.value.before.total_gross_minutes), after: formatDuration(preview.value.after.total_gross_minutes) },
  { label: i18n.t('print.report.columns.net'), before: formatDuration(preview.value.before.total_net_minutes), after: formatDuration(preview.value.after.total_net_minutes) },
  { label: i18n.t('print.report.columns.pause'), before: formatDuration(preview.value.before.total_pause_minutes), after: formatDuration(preview.value.after.total_pause_minutes) },
  { label: i18n.t('common.report.expensesReference'), before: money(preview.value.before.total_expenses_reference), after: money(preview.value.after.total_expenses_reference) },
  { label: i18n.t('common.report.keptDroppedEdits'), before: preview.value.kept_edits, after: preview.value.dropped_edits },
]

onMounted(async() => { preview.value = await accountingStore.recomputeReportAsync(props.report.id, true) })

const onConfirm = async() => {
  isSaving.value = true
  await accountingStore.recomputeReportAsync(props.report.id)
  isSaving.value = false
  emit('recomputed')
}
</script>

<template>
  <q-dialog :model-value="true" class="dialog-xxs" @hide="emit('cancel')">
    <q-card>
      <q-card-section class="row items-center q-gutter-sm">
        <q-avatar
          icon="mdi-refresh"
          color="primary"
          text-color="white"
          size="md"
        />
        <span class="text-h6">{{ i18n.t('common.report.recomputeTitle', { name: props.report.name }) }}</span>
      </q-card-section>
      <q-card-section class="q-pt-none">
        <div class="q-mb-sm">{{ i18n.t('common.report.recomputeMessage') }}</div>
        <q-inner-loading :showing="preview === null" />
        <q-markup-table
          v-if="preview"
          flat
          dense
          bordered
        >
          <thead><tr><th class="text-left" /><th class="text-right">{{ i18n.t('common.report.now') }}</th><th class="text-right">{{ i18n.t('common.report.after') }}</th></tr></thead>
          <tbody>
            <tr v-for="line in lines()" :key="line.label"><td>{{ line.label }}</td><td class="text-right">{{ line.before }}</td><td class="text-right">{{ line.after }}</td></tr>
          </tbody>
        </q-markup-table>
      </q-card-section>
      <q-card-actions align="right">
        <q-btn flat :label="i18n.t('common.cancel')" @click="emit('cancel')" />
        <q-btn
          color="primary"
          :loading="isSaving"
          :disable="preview === null"
          :label="i18n.t('common.report.recompute')"
          @click="onConfirm"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
